import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import PlagiarismOutlinedIcon from '@mui/icons-material/PlagiarismOutlined';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { isEqual } from 'lodash';
import { useId, useState } from 'react';

import {
  Alert,
  NotableDataGrid,
  api,
  formatFileSize,
  getAndValidateFirstFileFromFileList,
  getCursorBasedEstimatedRowCount,
  isDefined,
  useLoggedInAuthContext,
  useQueryParams,
  useToast,
} from 'common';

import { DialogCloseButton } from '../../../../../../../../components/DialogCloseButton';
import { AutocompleteFilter } from '../../../../../../../../components/FiltersV2/Autocomplete';
import { TextSearch } from '../../../../../../../../components/FiltersV2/TextSearch';
import { OrganizationLabel } from '../../../../../../../../components/Label/Organization';
import { FilterBar } from '../../../../../../../../components/Layout/FilterBar';
import { Time } from '../../../../../../../../components/Time';
import { UserLink } from '../../../../../../../../components/links/User';
import { useCursorPaginationApi } from '../../../../../../../../hooks/useCursorPaginationApi';
import { EMPTY_CONTENT } from '../../../../../../../../utils/constants';
import { useUploadFileContext } from '../../../../../../../KnowledgeBase/fileUploadContext';
import { useFlowDefinitionContext } from '../../../../../context';

type KnowledgeBaseFile = api.FlowDefinition.KnowledgeBaseFile;

interface FileSelectDialogProps {
  onClose: () => void;
  selectedKnowledgeBaseFileIds?: string[];
  setSelectedKnowledgeBaseFileIds: (knowledgeBaseFileIds?: string[]) => void;
  onClickUseFileId: () => void;
}

export function FileSelectDialog(props: FileSelectDialogProps) {
  const {
    onClose,
    selectedKnowledgeBaseFileIds,
    setSelectedKnowledgeBaseFileIds,
    onClickUseFileId,
  } = props;
  const {
    flowDefinition: { organizationId },
  } = useFlowDefinitionContext();
  const { setFile, registerInputFileRef } = useUploadFileContext();
  const id = useId();
  const { showError } = useToast();
  const { hasPermission } = useLoggedInAuthContext();
  const { queryParams, updateQueryParam } = useQueryParams({
    schema: api.KnowledgeBaseFile.KnowledgeBaseFileFindAllOptionsSchema.pick({
      name: true,
      displayedType: true,
    }),
  });

  const {
    isPending,
    error,
    data: knowledgeBaseFilesResponse,
    pagination,
    sort,
  } = useCursorPaginationApi({
    api: api.client.KnowledgeBaseFile,
    queryParams: {
      organizationId,
      name: queryParams.name,
      displayedType: queryParams.displayedType,
    },
  });

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(selectedKnowledgeBaseFileIds ?? []),
  });

  const columns: GridColDef<KnowledgeBaseFile>[] = [
    {
      field: 'name',
      headerName: 'File name',
      flex: 10,
    },
    {
      field: 'displayedType',
      headerName: 'Type',
      flex: 3,
    },
    {
      field: 'dataSizeBytes',
      headerName: 'Size',
      flex: 3,
      renderCell: ({ row }) => formatFileSize(row.dataSizeBytes),
    },
    {
      field: 'category',
      headerName: 'Categories',
      flex: 6,
    },
    {
      field: 'uploadedById',
      headerName: 'Uploaded by',
      flex: 6,
      renderCell: ({ row }) =>
        isDefined(row.uploadedById) ? <UserLink userId={row.uploadedById} /> : EMPTY_CONTENT,
      sortable: false,
    },
    {
      field: 'created',
      headerName: 'Upload date',
      renderCell: ({ row }) => <Time when={row.created} />,
      flex: 6,
    },
  ];

  return (
    <>
      <Dialog open={true} fullWidth maxWidth="xl" onClose={onClose}>
        <DialogTitle>Browse Knowledge Base Files</DialogTitle>
        <DialogCloseButton onClose={onClose} />
        <DialogContent>
          <Stack gap={2}>
            <FilterBar>
              <TextSearch
                initialValue={queryParams.name}
                setValue={(value) => {
                  updateQueryParam('name', value);
                  pagination.goToFirstPage();
                }}
              />
              <Stack direction="row" alignItems="center">
                <OrganizationLabel organizationIds={[organizationId]} />
              </Stack>
              <AutocompleteFilter
                label="Type"
                value={queryParams.displayedType}
                setValue={(value) => {
                  updateQueryParam('displayedType', value);
                  pagination.goToFirstPage();
                }}
                options={Array.from(api.KnowledgeBaseFile.VALID_EXTENSIONS).sort((a, b) =>
                  a.localeCompare(b)
                )}
              />
            </FilterBar>
            {isDefined(error) && (
              <Alert title="Error loading knowledge base documents" error={error} />
            )}
            <NotableDataGrid
              height="60vh"
              rowCount={getCursorBasedEstimatedRowCount(
                knowledgeBaseFilesResponse,
                pagination.paginationModel
              )}
              pagination={pagination}
              sort={sort}
              isPending={isPending}
              data={knowledgeBaseFilesResponse?.data ?? []}
              columns={columns}
              checkboxSelection
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={(newRowSelectionModel) => {
                setRowSelectionModel(newRowSelectionModel);
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Stack direction="row" justifyContent="space-between" width="100%">
            <Stack direction="row" justifyContent="flex-start" gap={2}>
              {hasPermission.UpdateKnowledgeBaseFiles && (
                <Button variant="outlined" component="label" startIcon={<FileUploadOutlinedIcon />}>
                  <input
                    hidden
                    type="file"
                    ref={(ref) => {
                      registerInputFileRef(id, ref);
                    }}
                    onChange={(event) => {
                      const { files } = event.target;
                      if (!isDefined(files)) {
                        return;
                      }
                      try {
                        const file = getAndValidateFirstFileFromFileList({
                          files,
                          validContentTypes: api.KnowledgeBaseFile.VALID_CONTENT_TYPES,
                          maxFileSizeInBytes:
                            api.KnowledgeBaseFile.MAX_KNOWLEDGE_BASE_FILE_SIZE_IN_BYTES,
                        });
                        setFile(file);
                      } catch (error: unknown) {
                        if (error instanceof Error) {
                          showError(error.message);
                        }
                      }
                      onClose();
                    }}
                  />
                  Pick a file to upload
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={onClickUseFileId}
                startIcon={<PlagiarismOutlinedIcon />}
              >
                Use file ID
              </Button>
            </Stack>
            <Stack direction="row" justifyContent="flex-end" gap={2}>
              <Button variant="text" onClick={() => onClose()}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  const newKnowledgeBaseFileIds = knowledgeBaseFilesResponse?.data
                    .filter((file) => rowSelectionModel.ids.has(file.id))
                    .map((file) => file.id);
                  setSelectedKnowledgeBaseFileIds(newKnowledgeBaseFileIds);
                  onClose();
                }}
                disabled={isEqual(rowSelectionModel.ids, new Set(selectedKnowledgeBaseFileIds))}
              >
                Update files
              </Button>
            </Stack>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
}
